import { useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import Tooltip from '@/components/Tooltip';
import { GgcButton } from '@/components/ui/ggc/button';
import { useI18nText } from '@/lib/utils/i18nText';

/** One place an inclusion-comment token currently lives and can be deleted from. */
export type CommentDeleteTarget = {
  /** Save target — a campaign objectId, or 'default' for the influencer's suggested comment. */
  campaignId: string;
  /** Campaign name (ignored when `isDefault`). */
  label: string;
  isDefault: boolean;
  /** Deletion not allowed from this target (e.g. default comment without permission). */
  disabled?: boolean;
  disabledReason?: string;
};

/**
 * A blue inclusion-comment chip whose × deletes the token. A token can live in
 * several places at once (the influencer's default comment + one or more
 * campaigns), so the × resolves the ambiguity in the action itself instead of
 * being hidden:
 *  - no deletable target  → × shown disabled with a reason (RemovableTag-style),
 *  - exactly one target    → a confirm popover naming that target,
 *  - multiple targets      → a chooser: delete from one, or from all at once.
 * The popover is portaled so it escapes the table cell's overflow:hidden.
 */
export function MultiTargetCommentTag({ token, isSuggested, targets, blockedReason, onRemove }: {
  token: string;
  isSuggested?: boolean;
  targets: CommentDeleteTarget[];
  /** Reason there's no writable target at all (e.g. token only exists in a draft with no id). */
  blockedReason?: string;
  onRemove: (campaignIds: string[]) => Promise<void>;
}) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const chipClass = `ggc-tag ggc-tag--default ggc-tag--removable${isSuggested ? ' ggc-tag--suggested' : ''}`;
  const deletable = targets.filter((tg) => !tg.disabled);
  const targetName = (tg: CommentDeleteTarget) =>
    tg.isDefault ? t('the default comment', '기본 코멘트') : (tg.label || t('a private draft', '비공개 초안'));

  // Nothing can be deleted from here: keep the affordance visible but disabled with
  // a reason, rather than hiding it (which reads as "this can never be deleted").
  if (deletable.length === 0) {
    const reason = blockedReason
      ?? targets.find((tg) => tg.disabledReason)?.disabledReason
      ?? t("This comment can't be deleted here.", '여기서는 이 코멘트를 삭제할 수 없습니다.');
    return (
      <span className={chipClass}>
        {token}
        <Tooltip content={reason}>
          <button type="button" className="ggc-tag__remove" aria-label={t('Delete comment', '코멘트 삭제')} disabled>
            <i className="fa fa-times" />
          </button>
        </Tooltip>
      </span>
    );
  }

  const run = async (campaignIds: string[]) => {
    setBusy(true);
    try {
      await onRemove(campaignIds);
    } catch { /* surfaced by the global error handler */ } finally {
      setBusy(false);
    }
    // Don't force-close: the parent recomputes `targets` from what actually saved,
    // so the popover naturally shrinks (multi → fewer rows) or the chip unmounts.
  };

  const removeButton = (
    <button
      type="button"
      className="ggc-tag__remove"
      aria-label={t('Delete comment', '코멘트 삭제')}
      onClick={(e) => { e.stopPropagation(); setOpen(true); }}
    >
      <i className="fa fa-times" />
    </button>
  );

  const single = targets.length === 1;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
      <PopoverPrimitive.Anchor asChild>
        <span className={chipClass}>
          {token}
          {/* Hide the hover hint once the popover is open so the two don't stack. */}
          {open ? removeButton : (
            <Tooltip essential content={t('Click to delete this comment', '클릭하면 이 코멘트가 삭제됩니다')}>
              {removeButton}
            </Tooltip>
          )}
        </span>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="bottom"
          align="center"
          sideOffset={6}
          collisionPadding={8}
          className="tag-delete-popover"
          onClick={(e) => e.stopPropagation()}
        >
          {single ? (
            <>
              {/* Name the target: from the aggregate row the admin can't otherwise tell
                  which comment (default vs a campaign they never opened) they're editing. */}
              <div className="tag-delete-popover__msg">{t('Delete comment', '코멘트 삭제')} <strong>{token}</strong>?</div>
              <div className="tag-delete-popover__target-note">{t('From', '대상')}: {targetName(targets[0])}</div>
            </>
          ) : (
            <>
              <div className="tag-delete-popover__msg">
                <strong>{token}</strong> — {t('choose where to delete it', '어디서 삭제할지 선택하세요')}
              </div>
              <div className="tag-delete-popover__targets">
                {targets.map((tg) => (
                  <div key={tg.campaignId} className="tag-delete-popover__target-row">
                    <span className="tag-delete-popover__target-name">{targetName(tg)}</span>
                    {tg.disabled ? (
                      <Tooltip content={tg.disabledReason ?? ''}>
                        <GgcButton variant="secondary" size="xs" disabled>{t('Delete', '삭제')}</GgcButton>
                      </Tooltip>
                    ) : (
                      <GgcButton variant="danger" size="xs" disabled={busy} onClick={() => void run([tg.campaignId])}>
                        {t('Delete', '삭제')}
                      </GgcButton>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
          <div className="tag-delete-popover__actions">
            <GgcButton variant="secondary" size="xs" disabled={busy} onClick={() => setOpen(false)}>
              {t('Cancel', '취소')}
            </GgcButton>
            {single ? (
              <GgcButton variant="danger" size="xs" disabled={busy} onClick={() => void run([targets[0].campaignId])}>
                {busy
                  ? <><i className="fa fa-spinner fa-spin mr-1" />{t('Deleting...', '삭제 중...')}</>
                  : <><i className="fa fa-trash mr-1" />{t('Delete', '삭제')}</>}
              </GgcButton>
            ) : (
              <GgcButton variant="danger" size="xs" disabled={busy} onClick={() => void run(deletable.map((d) => d.campaignId))}>
                {busy
                  ? <><i className="fa fa-spinner fa-spin mr-1" />{t('Deleting...', '삭제 중...')}</>
                  : <><i className="fa fa-trash mr-1" />{t('Delete from all', '모두 삭제')} ({deletable.length})</>}
              </GgcButton>
            )}
          </div>
          <PopoverPrimitive.Arrow className="fill-white" width={12} height={6} />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
